import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { employeeApi, CreateEmployeeInput, UpdateEmployeeInput } from "@/api/employee.api";

export function useActiveEmployees() {
  return useQuery({
    queryKey: ["employees", "active"],
    queryFn: employeeApi.listActive,
  });
}

export function useEmployees(params: { page: number; limit: number; search?: string; status?: string }) {
  return useQuery({
    queryKey: ["employees", "list", params],
    queryFn: () => employeeApi.list(params),
    placeholderData: (prev) => prev,
  });
}

export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: ["employees", id],
    queryFn: () => employeeApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useEmployeeJobs(id: string | undefined, page = 1, limit = 10) {
  return useQuery({
    queryKey: ["employees", id, "jobs", page, limit],
    queryFn: () => employeeApi.getJobs(id as string, { page, limit }),
    enabled: Boolean(id),
  });
}

function useInvalidateEmployees() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["employees"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };
}

export function useCreateEmployee() {
  const invalidate = useInvalidateEmployees();
  return useMutation({
    mutationFn: (input: CreateEmployeeInput) => employeeApi.create(input),
    onSuccess: invalidate,
  });
}

export function useUpdateEmployee() {
  const invalidate = useInvalidateEmployees();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEmployeeInput }) => employeeApi.update(id, input),
    onSuccess: invalidate,
  });
}

export function useUpdateEmployeeStatus() {
  const invalidate = useInvalidateEmployees();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: "ACTIVE" | "INACTIVE" | "ON_LEAVE" }) =>
      employeeApi.updateStatus(id, status),
    onSuccess: invalidate,
  });
}

export function useDeleteEmployee() {
  const invalidate = useInvalidateEmployees();
  return useMutation({
    mutationFn: (id: string) => employeeApi.remove(id),
    onSuccess: invalidate,
  });
}
